import exifr from "exifr";

/*
 * EXIF metadata for uploaded images may contain rotations that are missed by the backend
 * when uploading. This utility rotates the actual image to reflect the correct orientation so
 * that the image doesn't depend on the preservation of the metadata.
 *
 * Sample images to test orientations are available here:
 * https://github.com/recurser/exif-orientation-examples
 */
export async function fixImageEXIFOrientation(
  src: File,
): Promise<File | undefined> {
  // This exif rotation issue only affects jpeg images
  if (src.type !== "image/jpeg") {
    return src;
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (ctx === null) {
    throw new Error(
      "Could not get canvas context for image rotation correction",
    );
  }
  const img = await fileToImage(src, 10000);

  // oxlint-disable-next-line import/no-named-as-default-member
  const ro = await exifr.rotation(img);
  if (ro === undefined) {
    return src;
  }

  const w = img.width;
  const h = img.height;
  canvas.width = w;
  canvas.height = h;

  ctx.drawImage(img, 0, 0, w, h);
  ctx.translate(w / 2, h / 2);
  ctx.rotate(ro.rad);
  ctx.scale(ro.scaleX, ro.scaleY);

  const correctedFile = await canvasToFile(
    canvas,
    src.name,
    src.type, // You can change the output format here
  );
  return correctedFile;
}

async function fileToImage(
  file: File,
  timeoutMs = 10000,
): Promise<HTMLImageElement> {
  // Ensure the file is an image
  if (!file.type.startsWith("image/")) {
    throw new Error("Provided file is not an image.");
  }

  const dataUrl = await fileToDataURL(file);
  if (dataUrl === null) {
    throw new Error("Empty data URL while converting file to image");
  }
  return await dataURLToImage(dataUrl, timeoutMs);
}

function dataURLToImage(
  dataURL: string,
  timeoutMs: number,
): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    let timeoutId = -1;

    const img = new Image();
    const cleanup = () => {
      URL.revokeObjectURL(dataURL); // Clean up the object URL
      clearTimeout(timeoutId); // Clear the timeout if image loads/errors before timeout
    };

    img.onload = () => {
      cleanup();
      resolve(img);
    };

    img.onerror = () => {
      cleanup();
      reject(new Error("Failed to load image from file."));
    };

    // Set a timeout to reject the promise if loading takes too long
    timeoutId = window.setTimeout(() => {
      cleanup();
      reject(
        new Error(`Image loading timed out after ${timeoutMs / 1000} seconds.`),
      );
    }, timeoutMs);

    img.src = dataURL;
  });
}

function fileToDataURL(file: File): Promise<string | null> {
  return new Promise((resolve, reject) => {
    // 1. Create a new FileReader instance
    const reader = new FileReader();

    // 2. Set up the onload event handler
    // This event fires when the file has been successfully read.
    reader.onload = () => {
      resolve(reader.result as string); // reader.result contains the data URL
    };

    // 3. Set up the onerror event handler
    reader.onerror = (error) => {
      reject(error);
    };

    // 4. Start the reading process
    // This reads the file's content and triggers the onload event when done.
    reader.readAsDataURL(file);
  });
}

// Converts a canvas element to a File object.
function canvasToFile(
  canvas: HTMLCanvasElement,
  fileName: string,
  fileType: string = "image/jpeg",
  quality: number = 0.92,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return reject(new Error("Canvas toBlob returned null."));
        }
        const file = new File([blob], fileName, { type: fileType });
        resolve(file);
      },
      fileType,
      quality,
    );
  });
}
